import { useMsal } from "@azure/msal-react";
import {
  HOTJAR_SITE_ID_PROD,
  HOTJAR_SITE_ID_TEST,
  HOTJAR_VERSION,
} from "config/constants";
import { isProdEnv } from "config/env";
import { functionPermissions } from "config/function-permissions";
import { useCallback, useEffect } from "react";
import { hotjar } from "react-hotjar";
import useSWR from "swr";

export type PermissionSection =
  | "Admin"
  | "Cost"
  | "CustomerService"
  | "DailyStatus"
  | "Forecast"
  | "Inventory"
  | "Order"
  | "Quality"
  | "User";
export type Permission = "Create" | "Read" | "Update" | "Delete";
export type Mapping = { [key: string]: any };

export interface UserResponse {
  id: string;
  name: string;
  email: string;
  roles: string[];
  functionPermissions: Array<{
    area: string;
    name: string;
    id: number;
    moduleName: string | null;
  }>;
  modules: {
    name: PermissionSection;
    permissions: {
      name: Permission;
      value: boolean;
    }[];
  }[];
  connectedCustomers: string[];
  favoriteCustomers: string[];
  startPage: string | null;
  isInternalUser: boolean;
}

export const getDefaultPathForPermissions = (
  hasPermissionForFunctions: (
    permission: functionPermissions,
  ) => boolean | undefined,
) => {
  const routes = [
    {
      permission: functionPermissions.AccessDailyStatus,
      path: `/daily-status`,
    },
    { permission: functionPermissions.AccessCost, path: `/cost-control/cost` },
    {
      permission: functionPermissions.AccessOrder,
      path: `/cost-control/order`,
    },
    {
      permission: functionPermissions.AccessInventory,
      path: `/cost-control/inventory`,
    },
    {
      permission: functionPermissions.AccessFreights,
      path: `/cost-control/freights`,
    },
    { permission: functionPermissions.AccessForecast, path: `/forecast` },
    {
      permission: functionPermissions.AccessQuality,
      path: `/quality/delivery-accuracy`,
    },
    { permission: functionPermissions.ShowAllInvoices, path: `/invoices` },
    {
      permission: functionPermissions.AccessHelpCenter,
      path: `/customer-service/help-center`,
    },
    {
      permission: functionPermissions.AccessAdminweb,
      path: `/admin/customers`,
    },
    { permission: functionPermissions.ListAllUsers, path: `/users` },
  ];
  const matching = routes.find((route) =>
    hasPermissionForFunctions(route.permission),
  );
  return matching?.path ?? false;
};

export const useUser = () => {
  const { accounts } = useMsal();
  const account = accounts[0];
  const {
    data: user,
    isLoading,
    mutate,
  } = useSWR<UserResponse>(account?.localAccountId ? `/Users/me` : null);

  useEffect(() => {
    if (
      user &&
      typeof window !== "undefined" &&
      window.Cookiebot &&
      window.Cookiebot?.consent &&
      window.Cookiebot?.consent.marketing
    ) {
      if (!hotjar.initialized()) {
        console.log(
          "Hotjar initialized for site:",
          isProdEnv ? "prod" : "test",
        );
        hotjar.initialize(
          isProdEnv ? HOTJAR_SITE_ID_PROD : HOTJAR_SITE_ID_TEST,
          HOTJAR_VERSION,
        );
      }
      hotjar.identify(user.id, {
        name: user.name,
        email: user.email,
        is_internal_user: String(user.isInternalUser),
      });
    }
  }, [user]);

  const handleHasPermissionForFunctions = useCallback(
    (permissionId: functionPermissions) => {
      return user?.functionPermissions.some(
        (current) => current.id === permissionId,
      );
    },
    [user?.functionPermissions],
  );

  return {
    account,
    user,
    mutate,
    isLoading,
    hasPermissionForFunctions: handleHasPermissionForFunctions,
  };
};
